import React from 'react';
import { Amplify } from '@aws-amplify/core';
import { useMachine } from '@xstate/react';
import {
  AuthContext,
  AuthEvent,
  authMachine,
  AuthStateSchema,
} from '@/utils/authMachine';
import options, { getUser } from '@/utils/AmplifyMachineConfig';
import { recallAuthDestination } from '@/utils/authDestination';
import { client } from '../graphql/graphql';
import { useHistory, useLocation } from 'react-router-dom';
import {
  BaseActionObject,
  ResolveTypegenMeta,
  ServiceMap,
  State,
  TypegenDisabled,
} from 'xstate';

export { constants } from './authMachine';

type MachineState = State<
  AuthContext,
  AuthEvent,
  AuthStateSchema,
  any,
  ResolveTypegenMeta<TypegenDisabled, AuthEvent, BaseActionObject, ServiceMap>
>;

type AuthStateContextType = Pick<MachineState, 'matches'> &
  Pick<
    AuthContext,
    | 'user'
    | 'destination'
    | 'error'
    | 'loading'
    | 'emailConfirmationDestination'
    | 'forgotPasswordDelivery'
    | 'partialProfile'
  > & { hasCompany: boolean; state: MachineState['value'] };

type AuthActionsContextType = {
  refreshUser: () => Promise<MachineState>;
  forgotPassword: (
    username: string,
    code: string,
    new_password: string
  ) => MachineState;
  signIn: (username: string, password: string) => MachineState;
  signUp: (
    username: string,
    password: string,
    email: string,
    attributes: { first_name: string; last_name: string }
  ) => MachineState;
  confirmEmail: (code: string) => MachineState;
  resendConfirmation: () => MachineState;
  signOut: (options?: { dontReload?: boolean }) => MachineState;
  submit: (data: any) => MachineState;
  retry: () => MachineState;
  resetLogin: () => MachineState;
  clearDestination: () => MachineState;
};

const AuthActionsContext = React.createContext<AuthActionsContextType>(
  {} as AuthActionsContextType
);
const AuthStateContext = React.createContext<AuthStateContextType>(
  {} as AuthStateContextType
);

export const useAuthState = () => {
  return React.useContext(AuthStateContext);
};
export const useAuthActions = () => {
  return React.useContext(AuthActionsContext);
};

export const AuthMachineProvider: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const history = useHistory();
  const location = useLocation();
  const [current, send, service] = useMachine(authMachine, {
    ...options,
    context: {
      Auth: Amplify.Auth,
      GraphQLClient: client,
      Navigator: { history, location },
      // Mounted again right after signing in (authDestination.ts).
      destination: recallAuthDestination(),
    },
    devTools: true,
  });

  React.useEffect(() => {
    if (import.meta.env.NODE_ENV === 'development') {
      service.onTransition((state) => {
        if (state.changed) console.log(state);
      });
    }
  }, [service]);

  const stateContext = React.useMemo(
    () => ({
      matches: current.matches,
      state: current.value,
      user: current.context.user,
      hasCompany: !!current.context.user?.roles[0],
      destination: current.context.destination,
      error: current.context.error,
      loading: current.context.loading,
      emailConfirmationDestination:
        current.context.emailConfirmationDestination,
      forgotPasswordDelivery: current.context.forgotPasswordDelivery,
      partialProfile: current.context.partialProfile,
    }),
    [
      current.context.destination,
      current.context.emailConfirmationDestination,
      current.context.error,
      current.context.forgotPasswordDelivery,
      current.context.loading,
      current.context.partialProfile,
      current.context.user,
      current.matches,
      current.value,
    ]
  );

  const actionsContext: AuthActionsContextType = React.useMemo(
    () => ({
      refreshUser: () =>
        getUser({ GraphQLClient: client }, undefined, 'network-only').then(
          (user) => send({ type: 'REFRESH_USER', data: user })
        ),
      forgotPassword: (username, code, new_password) =>
        send({
          type: 'FORGOT_PASSWORD',
          data: { username, code, new_password },
        }),
      signIn: (username, password) =>
        send({ type: 'SIGNIN', data: { username, password } }),
      signUp: (username, password, email, attributes) =>
        send({
          type: 'SIGNUP',
          data: { username, password, email, attributes },
        }),
      confirmEmail: (code) =>
        send({
          type: 'CONFIRM_EMAIL',
          data: { code },
        }),
      resendConfirmation: () =>
        send({
          type: 'RESEND_CONFIRM_EMAIL',
        }),
      signOut: (options) => send({ type: 'SIGNOUT', data: options }),
      submit: (data) =>
        send({
          type: 'SUBMIT',
          data: data,
        }),
      retry: () => send({ type: 'RETRY' }),
      resetLogin: () => send({ type: 'RESET' }),
      clearDestination: () => send({ type: 'CLEAR_DESTINATION' }),
    }),
    [send]
  );

  return (
    <AuthStateContext.Provider value={stateContext}>
      <AuthActionsContext.Provider value={actionsContext}>
        {children}
      </AuthActionsContext.Provider>
    </AuthStateContext.Provider>
  );
};
